import { Avatar, Box, Button, Chip, Typography, makeStyles } from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { parseEntityRef, Entity } from '@backstage/catalog-model';
import { getApplicationType } from './config';
import { getEnvironment, getDisplayName, getOwnerRef, getEndpointLink } from './entityHelpers';

const useStyles = makeStyles(theme => ({
  hero: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
    padding: theme.spacing(3, 4),
    borderRadius: theme.shape.borderRadius * 2,
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3),
  },
  identity: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2.5),
    minWidth: 0,
  },
  avatar: {
    width: 60,
    height: 60,
    fontSize: '1.5rem',
    fontWeight: 700,
    flexShrink: 0,
  },
  name: {
    fontWeight: 700,
    lineHeight: 1.2,
    wordBreak: 'break-word',
  },
  chipsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    flexWrap: 'wrap',
  },
  ctaButton: {
    borderRadius: 999,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    whiteSpace: 'nowrap',
  },
}));

// Small, deliberately generic palette — not tied to any specific app type,
// since spec.type is open-ended and driven entirely by config.ts.
const ENV_COLORS: Record<string, string> = {
  PRODUCTION: '#c62828',
  PROD: '#c62828',
  STAGING: '#ef6c00',
  STAGE: '#ef6c00',
  DEVELOPMENT: '#1565c0',
  DEV: '#1565c0',
};

function envColor(env?: string): string {
  if (!env) return '#616161';
  return ENV_COLORS[env] ?? '#616161';
}

export interface ApplicationHeroProps {
  entity: Entity;
  type: string;
}

export function ApplicationHero({ entity, type }: ApplicationHeroProps) {
  const classes = useStyles();
  const typeConfig = getApplicationType(type);
  const env = getEnvironment(entity);
  const ownerRef = getOwnerRef(entity);
  const endpoint = getEndpointLink(entity);
  const name = getDisplayName(entity);
  const color = envColor(env);

  return (
    <Box className={classes.hero}>
      <Box className={classes.identity}>
        <Avatar className={classes.avatar} style={{ backgroundColor: color }}>
          {(typeConfig?.title ?? type).charAt(0).toUpperCase()}
        </Avatar>
        <Box minWidth={0}>
          <Typography variant="h4" className={classes.name}>
            {name}
          </Typography>
          {entity.metadata.description && (
            <Typography variant="body2" color="textSecondary">
              {entity.metadata.description}
            </Typography>
          )}
          <Box className={classes.chipsRow}>
            <Chip label={typeConfig?.title ?? type} size="small" variant="outlined" />
            {env && (
              <Chip
                label={env}
                size="small"
                style={{ backgroundColor: color, color: '#fff', fontWeight: 600 }}
              />
            )}
            {ownerRef && (
              <Typography variant="body2" component="span">
                <EntityRefLink
                  entityRef={parseEntityRef(ownerRef, { defaultKind: 'group', defaultNamespace: 'default' })}
                />
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      
      { endpoint?.url && (
        <Button
          className={classes.ctaButton}
          variant="contained"
          color="primary"
          size="large"
          disabled={!endpoint}
          href={endpoint?.url}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<OpenInNewIcon />}
        >
          {endpoint ? `Open ${typeConfig?.title ?? type}` : 'Endpoint not configured'}
        </Button>
      )}
    </Box>
  );
}
